import {
  HardDrives,
  Lightning,
  Bell,
  AddressBook,
  Globe,
  BookOpen,
  EnvelopeSimple,
  CheckSquare,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import type { SidebarActions } from '../../../stores/sidebarActionsStore';
import { isAppHidden } from '../../../apps/appDefinitions.index';
import { useIntegrationsPopupStore } from '../../../stores/integrationsPopupStore';

// ─── Sidebar header pins ──────────────────────────────────────────────────────

/**
 * Apps pinnable to the entity-sidebar header.
 *
 * Requires `handlerKey` (field on `sidebarActionsStore`) because the header
 * buttons invoke sidebar-registered callbacks, not `space_createWindow`.
 * `headerIcon` uses a bold Phosphor variant for crisp rendering at 12 px.
 */
export interface PinnableApp {
  appId: string;
  label: string;
  handlerKey: keyof SidebarActions;
  headerIcon: Icon;
}

export const PINNABLE_APPS: PinnableApp[] = [
  { appId: 'entityBrowser2', label: 'Drive', handlerKey: 'onOpenDriveApp', headerIcon: HardDrives },
  { appId: 'automations', label: 'Automations', handlerKey: 'onOpenAutomations', headerIcon: Lightning },
  { appId: 'notifications', label: 'Notifications', handlerKey: 'onOpenNotificationsApp', headerIcon: Bell },
  { appId: 'contacts', label: 'Contacts', handlerKey: 'onOpenContacts', headerIcon: AddressBook },
  { appId: 'browser', label: 'Browser', handlerKey: 'onOpenBrowserApp', headerIcon: Globe },
  { appId: 'writers_dashboard', label: 'Writing Dashboard', handlerKey: 'onOpenWritingDashboard', headerIcon: BookOpen },
  { appId: 'mail', label: 'Mail', handlerKey: 'onOpenMailApp', headerIcon: EnvelopeSimple },
  { appId: 'tasks', label: 'Tasks', handlerKey: 'onOpenTasksApp', headerIcon: CheckSquare },
];

export const PINNABLE_APPS_BY_ID: Record<string, PinnableApp> = Object.fromEntries(
  PINNABLE_APPS.map((app) => [app.appId, app]),
);

/** Apps pinned to the sidebar header for new users until they change them. */
export const DEFAULT_PINNED_APPS: string[] = ['entityBrowser2'];

// ─── Dock app registry ────────────────────────────────────────────────────────

/**
 * Any app that can be pinned to the Space Dock.
 *
 * The dock opens apps via `executeCommand('space_createWindow', { appType })`,
 * so no sidebar handler is needed — only an `appId` registered in
 * `appRegistryStore` and an icon in `iconService`.
 */
export interface DockableApp {
  appId: string;
  label: string;
  /** Shows an "Experimental" badge in Dock Preferences and the Store. */
  experimental?: boolean;
  /**
   * Pseudo-apps (Settings, Integrations) open an overlay instead of a
   * windowed AppInstance. When set, the Dock and SlimCreatePalette call this
   * instead of `createWindow`/`space_createWindow`, and skip all
   * window-lifecycle affordances (existing-window focus, "New Window",
   * "Show All Windows", "Quit") in their context menus.
   */
  action?: () => void;
}

export const DOCK_APPS: DockableApp[] = [
  // Core workspace
  { appId: 'entityBrowser2', label: 'Drive' },
  { appId: 'chat', label: 'Chat' },
  { appId: 'docs', label: 'Documents' },
  { appId: 'tasks', label: 'Tasks' },
  { appId: 'notifications', label: 'Notifications' },
  { appId: 'contacts', label: 'Contacts' },
  { appId: 'mail', label: 'Mail' },
  { appId: 'browser', label: 'Browser' },
  { appId: 'automations', label: 'Automations', experimental: true },
  { appId: 'sequences', label: 'Sequence Builder' },
  { appId: 'socialstudio', label: 'Social Studio' },
  // Documents & media
  { appId: 'page-v2', label: 'Page' },
  { appId: 'notes-v2', label: 'Notes' },
  { appId: 'canvas-v2', label: 'Canvas' },
  { appId: 'sheets-v2', label: 'Sheets' },
  { appId: 'presentations-v2', label: 'Presentations', experimental: true },
  { appId: 'pdfium', label: 'PDF Viewer' },
  { appId: 'mediagen', label: 'Media Gen', experimental: true },
  { appId: 'videostudio', label: 'Video Studio' },
  { appId: 'imagestudio', label: 'Image Studio' },
  { appId: 'listen-app', label: 'Listen', experimental: true },
  // Writing & publishing
  { appId: 'writers_dashboard', label: 'Writing Dashboard' },
  { appId: 'book_formatter_v2', label: 'Book Formatter', experimental: true },
  { appId: 'manuscript-review-report', label: 'Manuscript Review', experimental: true },
  // Finance & data
  { appId: 'bookkeeper', label: 'Bookkeeper' },
  { appId: 'bank', label: 'Bank', experimental: true },
  // Knowledge & AI
  { appId: 'prompts-v2', label: 'Prompts' },
  { appId: 'prompt-library', label: 'Prompt Library', experimental: true },
  { appId: 'neurvana', label: 'Neurvana', experimental: true },
  // System
  { appId: 'settings', label: 'Settings', action: () => window.dispatchEvent(new CustomEvent('openSettings')) },
  { appId: 'integrations', label: 'Integrations', action: () => useIntegrationsPopupStore.getState().open() },
  // Developer & power tools
  { appId: 'app-builder', label: 'App Builder', experimental: true },
  { appId: 'ingestWizard', label: 'Import Files', experimental: true },
  { appId: 'store', label: 'Store' },
  { appId: 'password-vault', label: 'Password Vault', experimental: true },
  // Physician tools
  { appId: 'phys-crcl', label: 'CrCl Calculator' },
  { appId: 'phys-icd10', label: 'ICD-10' },
  { appId: 'phys-icd10x', label: 'ICD-10 Extractor' },
  { appId: 'phys-em', label: 'E&M Coding' },
  { appId: 'phys-denial', label: 'Denial Codes' },
  { appId: 'phys-interactions', label: 'Drug Interactions' },
  { appId: 'phys-medtable', label: 'Medication Table', experimental: true },
  // Games
  { appId: 'pong', label: 'Pong' },
  { appId: 'rapids', label: 'Rapids' },
];

export const DOCK_APPS_BY_ID: Record<string, DockableApp> = Object.fromEntries(
  DOCK_APPS.map((app) => [app.appId, app]),
);

/**
 * DOCK_APPS filtered down to apps not marked `hidden` (or `experimental` for
 * sessions that can't use experimental features) in their own definition.ts
 * (the app manifest — see appDefinitions.index.ts). This is the list every
 * discovery surface (Dock, Dock Preferences, SlimCreatePalette, dock config
 * sanitization) should render — DOCK_APPS itself stays the full catalog so
 * hidden apps remain addressable by appId where needed.
 *
 * A function (not a cached array) so it reflects the live admin/experimental-
 * toggle state rather than whatever was true at module load.
 */
export function getVisibleDockApps(): DockableApp[] {
  return DOCK_APPS.filter((app) => !isAppHidden(app.appId));
}

/** appId set mirroring getVisibleDockApps(), for O(1) membership checks. */
export function getVisibleDockAppIds(): ReadonlySet<string> {
  return new Set(getVisibleDockApps().map((app) => app.appId));
}

/**
 * Default ordered app list for the Space Dock. The dock constants and full
 * config type live in `constants/dock.ts` (which imports from here to avoid
 * a circular dep).
 */
export const DOCK_DEFAULT_APPS: string[] = [
  'entityBrowser2',
  'docs',
  'notes-v2',
  'sheets-v2',
  'canvas-v2',
  'presentations-v2',
  'browser',
];
